import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Universe } from '@kaeliux/shared';

/**
 * Throws when the universe doesn't exist or was never initialized (no
 * `sunAges`) — matches `loadUniverseContext`'s server-side gate exactly
 * (issue #53 review: fabricating defaults here let a deleted/never-seeded
 * universe id survive in localStorage, sail past `App.tsx`'s gate, and
 * then 400 on every subsequent gameplay call with no way back to the picker
 * except logging out). Callers (`useUniverseStore.loadUniverse`) must not
 * commit `selectedUniverseId` before this resolves — see that store's doc
 * comment — and should call `clearSelectedUniverse()` on failure so the
 * picker reappears instead of wedging the session.
 */
export async function getUniverse(universeId: string): Promise<Universe> {
    const snap = await getDoc(doc(db, 'universes', universeId));
    const data = snap.data();
    if (!data || !data.sunAges || Object.keys(data.sunAges).length === 0) {
        // TODO: Who catches this?
        throw new Error(`Universe "${universeId}" is not initialized`);
    }
    return {
        id: universeId,
        name: data.name as string | undefined,
        resourceMultiplier: data.resourceMultiplier ?? 1,
        speedMultiplier: data.speedMultiplier ?? 1,
        flightMultiplier: data.flightMultiplier ?? 1,
        sunAges: data.sunAges,
    } as Universe;
}

/**
 * Every universe available to pick from, for `UniverseSelection` (issue #53)
 * — a direct client read, not a Cloud Function: `universes/{u}` is readable
 * by any authenticated user (`firestore.rules`), and there's no
 * gameplay-authoritative write happening here. Universes are created
 * exclusively by the operator-run scripts under
 * `apps/functions/src/scripts/`, which always set `sunAges`; a doc lacking
 * it (hand-crafted, or mid-creation) is filtered out rather than offered as
 * a pickable option — same "initialized" definition the server's
 * `loadUniverseContext` gate uses.
 */
export async function listUniverses(): Promise<Universe[]> {
    const snap = await getDocs(collection(db, 'universes'));
    return snap.docs
        .map((d) => {
            const data = d.data();
            return {
                id: d.id,
                name: data.name as string | undefined,
                resourceMultiplier: data.resourceMultiplier ?? 1,
                speedMultiplier: data.speedMultiplier ?? 1,
                flightMultiplier: data.flightMultiplier ?? 1,
                sunAges: data.sunAges ?? {},
            } as Universe;
        })
        .filter((universe) => Object.keys(universe.sunAges).length > 0)
        .sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id));
}
